import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { OptionalFilter } from './interface';
import SideFiltersContent from './SideFiltersContent';
import { FiFilter } from 'react-icons/fi';
import {
  clearSideFilter,
  setModalSideFilters,
  updateSiteFilter,
} from '../../modules/SearchDatePicker/searchDatePickerSlice';
import { isEmpty } from 'lodash';
import './SideFiltersModal.less';
import { Z_MOBILE_SHEET } from '../../utils/mobileLayers';
import ClearFiltersButton from './components/ClearFiltersButton';

interface DynamicSorting {
  name: string;
  label: string;
}

interface Props {
  optional_filters?: OptionalFilter[];
  starsGreaterThanRatingFilter?: boolean;
  pricingMaxLimitAndPlusFilter?: number | undefined;
  handleOnClear?: () => void;
  isLoading?: boolean;
  hasPartiallyLoaded?: boolean;
  additionalPrice?: number;
  freeCancellationTooltipText?: string;
  dynamicSorting?: DynamicSorting[];
  isOpen: boolean;
  onClose: () => void;
  filterEntityType?: 'dynamic_flights' | 'bundle_flights';
  isReturnFlight?: boolean;
  showPricePerPax?: boolean;
  // Filter source the PAGE reads. Local-filter modules keep filters in React
  // state, and some pages read redux `modalSideFilters` directly — in both
  // cases changes must go straight to that source (as the desktop sidebar
  // does) instead of being staged and applied to redux `sideFilters`.
  localSideFilters?: Record<string, any> | null;
  setLocalSideFilters?: (f: Record<string, any>) => void;
  parentUsesModalScope?: boolean;
  // active filters, shown as a badge in the sheet title
  activeFiltersCount?: number;
}

const SideFiltersModal = ({
  optional_filters,
  starsGreaterThanRatingFilter,
  pricingMaxLimitAndPlusFilter,
  handleOnClear,
  isLoading,
  hasPartiallyLoaded,
  additionalPrice = 0,
  freeCancellationTooltipText,
  dynamicSorting,
  isOpen,
  onClose,
  filterEntityType,
  isReturnFlight,
  showPricePerPax,
  localSideFilters,
  setLocalSideFilters,
  parentUsesModalScope = false,
  activeFiltersCount = 0,
}: Props) => {
  const isLocalFilterMode = !!localSideFilters && !!setLocalSideFilters;
  // Direct mode: no staging — the widgets write to the page's own source.
  const isDirectMode = isLocalFilterMode || parentUsesModalScope;
  const { general: general_t } = useSelector(
    (state: RootState) => state.config.translations
  );
  const dispatch = useDispatch();

  const { sideFilters: reduxSideFilters, modalSideFilters } = useSelector(
    (state: RootState) => state.search
  );

  // Local state for temporary filter changes
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize local filters when modal opens
  useEffect(() => {
    if (isOpen && !isDirectMode) {
      dispatch(setModalSideFilters({ ...reduxSideFilters }));
      setHasChanges(false);
    }
  }, [dispatch, isOpen, reduxSideFilters]);

  // Track changes in local filters
  useEffect(() => {
    if (isOpen && !isDirectMode) {
      const hasModifications =
        JSON.stringify(modalSideFilters) !== JSON.stringify(reduxSideFilters);
      setHasChanges(hasModifications);
    }
  }, [modalSideFilters, reduxSideFilters, isOpen]);

  const handleApplyFilters = () => {
    if (isDirectMode) {
      // Already applied as the user changed them — just show the results.
      onClose();
      return;
    }
    // Apply all local filter changes to redux
    Object.keys(modalSideFilters).forEach((key) => {
      if (modalSideFilters[key] !== reduxSideFilters[key]) {
        dispatch(updateSiteFilter({ key, value: modalSideFilters[key] }));
      }
    });

    // Close modal
    onClose();
  };

  const handleCancel = () => {
    // Revert staged changes (direct mode has nothing staged)
    if (!isDirectMode) {
      dispatch(setModalSideFilters({ ...reduxSideFilters }));
    }
    onClose();
  };

  if (!optional_filters || isEmpty(optional_filters)) {
    return null;
  }

  return (
    <Modal
      title={
        <div className="side-filters-modal__title">
          <FiFilter />
          {general_t.filters}
          {activeFiltersCount > 0 && (
            <span className="side-filters-modal__count">{activeFiltersCount}</span>
          )}
        </div>
      }
      open={isOpen}
      onCancel={handleCancel}
      centered
      width={600}
      // Phones only: above the map modal (1010) and floating page widgets.
      zIndex={Z_MOBILE_SHEET}
      // Rendered on phones only (SideFilters' mobile branch): slide up from
      // the bottom like a native sheet instead of antd's zoom-in.
      transitionName="ant-move-down"
      maskTransitionName="ant-fade"
      wrapClassName="side-filters-sheet-wrap"
      footer={[
        <ClearFiltersButton
          key="clear"
          optional_filters={optional_filters}
          handleOnClear={() => {
            handleOnClear && handleOnClear();
            onClose();
          }}
          withModalScopeParams={!isLocalFilterMode}
          inactiveMode="disabled"
          filterEntityType={filterEntityType}
          isReturnFlight={isReturnFlight}
          localSideFilters={localSideFilters}
          setLocalSideFilters={setLocalSideFilters}
        />,
        <Button
          key="apply"
          type="primary"
          className="btn-style-5"
          onClick={handleApplyFilters}
          disabled={!isDirectMode && !hasChanges}
        >
          {general_t.search}
        </Button>,
      ]}
      className="side-filters-modal"
    >
      <div className="side-filters-modal-content">
        <SideFiltersContent
          optional_filters={optional_filters}
          starsGreaterThanRatingFilter={starsGreaterThanRatingFilter}
          pricingMaxLimitAndPlusFilter={pricingMaxLimitAndPlusFilter}
          withModalScopeParams={!isLocalFilterMode}
          isLoading={isLoading}
          hasPartiallyLoaded={hasPartiallyLoaded}
          additionalPrice={additionalPrice}
          freeCancellationTooltipText={freeCancellationTooltipText}
          dynamicSorting={dynamicSorting}
          filterEntityType={filterEntityType}
          isReturnFlight={isReturnFlight}
          showPricePerPax={showPricePerPax}
          localSideFilters={localSideFilters}
          setLocalSideFilters={setLocalSideFilters}
        />
      </div>
    </Modal>
  );
};

export default SideFiltersModal;
